import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { format } from 'date-fns';
import { formatCurrency, formatQuantity, formatRate } from '@/utils/formatters';

// Sub-views
import SORegisterView from './sales-orders/SORegisterView';
import SODetailView from './sales-orders/SODetailView';
import SOLineItemDetailView from './sales-orders/SOLineItemDetailView';

// Shadcn
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ClientForm from '@/components/shared/ClientForm';
import SOPrint from '@/components/SOPrint';
import { Plus, Trash2, Layers, AlertCircle, AlertTriangle, Factory, ShoppingCart, Truck, DollarSign, Printer, Info, ArrowRightLeft } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesOrderPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven navigation
  const activeSOId = searchParams.get('so') || null;
  const activeLineItemId = searchParams.get('item') || null;

  // Core data
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [boms, setBoms] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [woDeliveryStatus, setWoDeliveryStatus] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const pageSize = 25;
  const totalPages = Math.ceil(totalOrders / pageSize);

  // Permissions
  const hasSalesOrdersAccess = user?.allowed_modules?.includes('sales_orders') || user?.is_owner;
  const canSeePrices = hasSalesOrdersAccess;
  const canEditSO = hasSalesOrdersAccess;
  const canCreateSO = hasSalesOrdersAccess;
  const canDeleteSO = hasSalesOrdersAccess;

  // Dialog states
  const [openCreate, setOpenCreate] = useState(false);
  const [openClient, setOpenClient] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [openVariantDialog, setOpenVariantDialog] = useState(false);
  const [openDispatchDialog, setOpenDispatchDialog] = useState(false);
  const [openPrint, setOpenPrint] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [showFulfillmentWarning, setShowFulfillmentWarning] = useState(false);
  const [showStockAvailableDialog, setShowStockAvailableDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, order: null, lineItem: null, impact: null, confirmText: '' });
  const [changeItemDialog, setChangeItemDialog] = useState({ open: false, lineItem: null, newItemId: '', reason: '', saving: false });

  // Form data
  const [orderForm, setOrderForm] = useState({ customer_id: '', delivery_address_id: '', delivery_date: '', target_production_date: '', line_items: [] });
  const [selectedClient, setSelectedClient] = useState(null);
  const [currentItem, setCurrentItem] = useState({ item_id: '', quantity: '', unit_price: 0, sizes: '', specs: '', remarks: '', fulfillment_type: '' });

  // Variant
  const [variantRequirements, setVariantRequirements] = useState(null);
  const [variantSelections, setVariantSelections] = useState({});
  const [pendingLineItem, setPendingLineItem] = useState(null);
  const [selectedLineItemForVariants, setSelectedLineItemForVariants] = useState(null);

  // Dispatch
  const [selectedDispatchItem, setSelectedDispatchItem] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({ quantity_dispatched: 0, invoice_number: '', lr_number: '', dispatch_date: new Date().toISOString().split('T')[0], remarks: '' });

  // Fulfillment override
  const [pendingFulfillmentChange, setPendingFulfillmentChange] = useState(null);
  const [stockAvailableData, setStockAvailableData] = useState(null);

  // Print
  const [printOrder, setPrintOrder] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const printRef = useRef(null);

  // Derived state
  const activeSO = orders.find(o => o.id === activeSOId) || null;
  const activeLineItem = activeSO?.line_items?.find(li => li.id === activeLineItemId) || null;
  const activeClient = activeSO ? clients.find(c => c.id === activeSO.customer_id) : null;

  // =========== NAVIGATION ===========
  const navigateToRegister = () => setSearchParams({});
  const navigateToSO = (soId) => setSearchParams({ so: soId });
  const navigateToLineItem = (lineItemId) => { if (activeSOId) setSearchParams({ so: activeSOId, item: lineItemId }); };

  // =========== DATA FETCHING ===========
  const fetchOrders = async (page = currentPage, search = searchTerm, status = statusFilter) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      if (search) params.append('search', search);
      if (status && status !== 'all') params.append('status', status);
      const [ordersRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/sales-orders?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/sales-orders/count?${search ? `search=${search}` : ''}${status && status !== 'all' ? `&status=${status}` : ''}`, { headers: getAuthHeader() })
      ]);
      setOrders(ordersRes.data); setTotalOrders(countRes.data.total);
    } catch (e) { toast.error('Failed to fetch orders'); }
    finally { setIsLoading(false); }
  };

  const fetchClients = async () => { try { const r = await axios.get(`${API_URL}/clients`, { headers: getAuthHeader() }); setClients(r.data); } catch (e) {} };
  // Lean fetch by default - images loaded on-demand when printing
  const fetchItems = async () => { try { const r = await axios.get(`${API_URL}/items`, { headers: getAuthHeader() }); setItems(r.data); } catch (e) {} };
  const fetchBoms = async () => { try { const r = await axios.get(`${API_URL}/bom`, { headers: getAuthHeader() }); setBoms(r.data); } catch (e) {} };
  const fetchWorkOrders = async () => { try { const r = await axios.get(`${API_URL}/work-orders`, { headers: getAuthHeader() }); setWorkOrders(r.data); } catch (e) {} };

  const fetchWODeliveryStatus = async (soId) => {
    try { const r = await axios.get(`${API_URL}/sales-orders/${soId}/wo-delivery-status`, { headers: getAuthHeader() }); setWoDeliveryStatus(prev => ({ ...prev, [soId]: r.data })); } catch (e) {}
  };
  const fetchAllWODeliveryStatus = async (ordersList) => {
    const withWO = ordersList.filter(o => workOrders.some(wo => wo.sales_order_id === o.id));
    for (const order of withWO) fetchWODeliveryStatus(order.id);
  };

  useEffect(() => { fetchOrders(1, ''); fetchClients(); fetchItems(); fetchBoms(); fetchWorkOrders(); }, []);
  useEffect(() => { if (orders.length > 0 && workOrders.length > 0) fetchAllWODeliveryStatus(orders); }, [orders, workOrders]);

  // =========== HELPERS ===========
  const enrichOrderWithMasterData = (order) => {
    if (!order) return order;
    const client = clients.find(c => c.id === order.customer_id);
    const enrichedLI = order.line_items?.map(li => {
      const freshItem = items.find(i => i.id === li.item_id);
      return { 
        ...li, 
        item_name: freshItem?.name || li.item_name, 
        item_image: freshItem?.image_url || li.item_image, 
        // Keep SO line item number (e.g., VEIMU0139L-01) as item_code - DO NOT overwrite with item master code
        item_code: li.item_code,
        // Store item master code separately if needed
        item_master_code: freshItem?.item_code || ''
      };
    }) || [];
    return { ...order, customer_name: client?.name || order.customer_name, client_code: client?.client_code || order.client_code, line_items: enrichedLI };
  };

  const calculateSOStatus = (order) => {
    const linked = workOrders.filter(wo => wo.sales_order_id === order.id);
    if (linked.length === 0) return { status: 'WO Pending', color: 'bg-gray-100 text-gray-700' };
    if (linked.every(wo => wo.status === 'completed')) return { status: 'Completed', color: 'bg-green-100 text-green-700' };
    if (linked.some(wo => wo.processes?.some(p => p.job_cards?.length > 0 || ['issued','in_progress','completed'].includes(p.status)))) return { status: 'In Process', color: 'bg-blue-100 text-blue-700' };
    return { status: 'WO Created', color: 'bg-yellow-100 text-yellow-700' };
  };

  const hasItemBOM = (itemId) => { const item = items.find(i => i.id === itemId); if (item?.bom_id) return true; return boms.some(bom => bom.finished_good_id === itemId); };
  const getBOMForItem = (itemId) => { const item = items.find(i => i.id === itemId); if (item?.bom_id) return boms.find(bom => bom.id === item.bom_id); return boms.find(bom => bom.finished_good_id === itemId); };
  const bomHasVariants = (bom) => { if (!bom) return false; return (bom.processes || []).some(p => (p.materials || []).some(m => m.is_variant && m.variant_group_id)); };
  const getDefaultFulfillmentType = (item) => item?.source_type === 'IP' ? 'Make' : 'Buy';
  const isFulfillmentContradiction = (item, ft) => ft !== getDefaultFulfillmentType(item);
  const formatSpecs = (specs) => { if (!specs) return ''; if (typeof specs === 'string') return specs; if (typeof specs === 'object') return Object.entries(specs).filter(([_, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n'); return ''; };
  // Filter FNG/SFG items for sales - uses category field (source of truth)
  const finishedGoods = items.filter(i => {
    const cat = i.category?.toUpperCase();
    return cat === 'FNG' || cat === 'SFG';
  });

  const getItemBOMStatus = (itemId, fulfillmentType, vs = []) => {
    if (fulfillmentType !== 'Make') return { status: 'ok', message: '' };
    const bom = getBOMForItem(itemId); if (!bom) return { status: 'no_bom', message: 'No BOM' };
    if (bomHasVariants(bom) && (!vs || vs.length === 0)) return { status: 'needs_variants', message: 'Needs variants' };
    return { status: 'ok', message: '' };
  };

  // =========== SEARCH & PAGINATION ===========
  const handleSearch = (e) => { const v = e.target.value; setSearchTerm(v); setCurrentPage(1); clearTimeout(window.soSearchTimeout); window.soSearchTimeout = setTimeout(() => fetchOrders(1, v), 500); };
  const handlePageChange = (p) => { setCurrentPage(p); fetchOrders(p, searchTerm); };

  // =========== FIX VARIANT STATUS ===========
  const fixVariantStatus = async (soId) => {
    try {
      const r = await fetch(`${API_URL}/sales-orders/${soId}/fix-variant-status`, { method: 'POST', headers: getAuthHeader() });
      if (r.ok) { const d = await r.json(); toast.success(`Status fixed: ${d.old_status} → ${d.new_status}`); fetchOrders(); } else toast.error('Failed');
    } catch (e) { toast.error('Error'); }
  };

  // =========== CREATE / EDIT SO ===========
  const resetForm = () => { setOrderForm({ customer_id: '', delivery_address_id: '', delivery_date: '', target_production_date: '', line_items: [] }); setCurrentItem({ item_id: '', quantity: '', unit_price: 0, sizes: '', specs: '', remarks: '' }); setSelectedClient(null); setEditOrder(null); };
  const handleEdit = (order) => {
    setEditOrder(order);
    const client = clients.find(c => c.id === order.customer_id); setSelectedClient(client || null);
    let tpd = '';
    if (order.target_production_date) tpd = format(new Date(order.target_production_date), 'yyyy-MM-dd');
    else if (order.delivery_date) { const d = new Date(order.delivery_date); d.setDate(d.getDate() - 5); tpd = format(d, 'yyyy-MM-dd'); }
    setOrderForm({ customer_id: order.customer_id, delivery_address_id: order.delivery_address_id || '', delivery_date: order.delivery_date ? format(new Date(order.delivery_date), 'yyyy-MM-dd') : '', target_production_date: tpd, line_items: order.line_items || [] });
    setOpenCreate(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (orderForm.line_items.length === 0) { toast.error('Add at least one line item'); return; }
    if (!orderForm.target_production_date) { toast.error('Target production date is required'); return; }
    try {
      const payload = { customer_id: orderForm.customer_id, delivery_address_id: orderForm.delivery_address_id || '', delivery_date: new Date(orderForm.delivery_date).toISOString(), target_production_date: new Date(orderForm.target_production_date).toISOString(), line_items: orderForm.line_items };
      let createdOrder = null;
      if (editOrder) {
        const r = await axios.put(`${API_URL}/sales-orders/${editOrder.id}`, payload, { headers: getAuthHeader() });
        r.data.cascaded_to_work_orders ? toast.success(`Updated. Cascaded to ${r.data.linked_work_orders_count} WO(s).`) : toast.success('Order updated');
      } else {
        const r = await axios.post(`${API_URL}/sales-orders`, payload, { headers: getAuthHeader() }); createdOrder = r.data;
        const s = r.data.auto_wo_summary;
        if (s) { const parts = []; if (s.wos_created > 0) parts.push(`${s.wos_created} WO(s)`); if (s.boms_pending > 0) parts.push(`${s.boms_pending} BOM pending`); if (s.variants_pending > 0) parts.push(`${s.variants_pending} variant(s) pending`); toast.success(`Order created${parts.length ? ': ' + parts.join(', ') : ''}`); }
        else toast.success('Order created');
      }
      setOpenCreate(false); resetForm(); await fetchOrders(); fetchWorkOrders();
      if (createdOrder && !editOrder) setTimeout(() => { setPaymentOrder(createdOrder); setOpenPayment(true); }, 500);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed to save');
    }
  };

  // =========== LINE ITEM MANAGEMENT ===========
  const addLineItem = async () => {
    if (!currentItem.item_id) return;
    const qty = parseInt(currentItem.quantity);
    if (!currentItem.quantity || isNaN(qty) || qty <= 0) {
      toast.error('Quantity is required');
      return;
    }
    const item = items.find(i => i.id === currentItem.item_id); if (!item) return;
    const autoSpecs = formatSpecs(item.specs); const finalSpecs = currentItem.specs || autoSpecs;
    const defaultFT = getDefaultFulfillmentType(item);
    const bom = getBOMForItem(item.id);
    if (bom) {
      const reqs = await fetchVariantRequirements(bom.id);
      if (reqs?.has_variants && reqs.variant_requirements.length > 0) {
        setPendingLineItem({ id: `line-${Date.now()}`, item_id: currentItem.item_id, item_code: '', item_name: item.name, item_image: item.image_url, specs: finalSpecs, sizes: currentItem.sizes || '', remarks: currentItem.remarks || '', quantity: qty, unit_price: currentItem.unit_price || item.price || 0, amount: qty * (currentItem.unit_price || item.price || 0), variant_selections: [], item_source_type: item.source_type || 'MP', fulfillment_type: defaultFT, is_fulfillment_override: false });
        setVariantRequirements(reqs); setVariantSelections({}); setOpenVariantDialog(true); return;
      }
    }
    const li = { id: `line-${Date.now()}`, item_id: currentItem.item_id, item_code: '', item_name: item.name, item_image: item.image_url, specs: finalSpecs, sizes: currentItem.sizes || '', remarks: currentItem.remarks || '', quantity: qty, unit_price: currentItem.unit_price || item.price || 0, amount: qty * (currentItem.unit_price || item.price || 0), variant_selections: [], item_source_type: item.source_type || 'MP', fulfillment_type: defaultFT, is_fulfillment_override: false };
    setOrderForm({ ...orderForm, line_items: [...orderForm.line_items, li] });
    setCurrentItem({ item_id: '', quantity: '', unit_price: 0, sizes: '', specs: '', remarks: '', fulfillment_type: '' });
  };
  const removeLineItem = (idx) => setOrderForm({ ...orderForm, line_items: orderForm.line_items.filter((_, i) => i !== idx) });

  // =========== FULFILLMENT TYPE ===========
  const handleFulfillmentTypeChange = async (idx, newFT) => {
    const li = orderForm.line_items[idx]; const item = items.find(i => i.id === li.item_id);
    if (isFulfillmentContradiction(item, newFT)) {
      if (item?.source_type === 'IP' && newFT === 'Buy') {
        try { const r = await axios.get(`${API_URL}/items/${item.id}/free-stock`, { headers: getAuthHeader() }); const fs = r.data.free_stock || 0;
          if (fs >= (li.quantity || 0)) { setStockAvailableData({ idx, newFulfillmentType: newFT, item, lineItem: li, freeStock: fs, orderQty: li.quantity || 0 }); setShowStockAvailableDialog(true); return; }
        } catch (e) {}
      }
      setPendingFulfillmentChange({ idx, newFulfillmentType: newFT, item, lineItem: li }); setShowFulfillmentWarning(true);
    } else updateLineItemFulfillment(idx, newFT, false);
  };
  const updateLineItemFulfillment = (idx, ft, override) => {
    const upd = [...orderForm.line_items]; const item = items.find(i => i.id === upd[idx].item_id);
    upd[idx] = { ...upd[idx], fulfillment_type: ft, item_source_type: item?.source_type || 'MP', is_fulfillment_override: override };
    setOrderForm({ ...orderForm, line_items: upd });
  };

  // =========== VARIANTS ===========
  const fetchVariantRequirements = async (bomId) => { try { const r = await axios.get(`${API_URL}/bom/${bomId}/variant-requirements`, { headers: getAuthHeader() }); return r.data; } catch (e) { return null; } };
  const handleSelectVariantsForLineItem = async (idx) => {
    const li = orderForm.line_items[idx]; const bom = getBOMForItem(li.item_id);
    if (!bom) { toast.error('No BOM found'); return; }
    const reqs = await fetchVariantRequirements(bom.id);
    if (!reqs?.has_variants) { toast.info('No variants'); return; }
    setVariantRequirements(reqs); setVariantSelections({}); setSelectedLineItemForVariants(idx); setPendingLineItem(li); setOpenVariantDialog(true);
  };
  const handleEditSOForVariants = async (order, li, idx) => {
    handleEdit(order);
    setTimeout(async () => {
      const bom = getBOMForItem(li.item_id);
      if (bom) { const reqs = await fetchVariantRequirements(bom.id); if (reqs?.has_variants) { setVariantRequirements(reqs); setVariantSelections({}); setSelectedLineItemForVariants(idx); setPendingLineItem(li); setOpenVariantDialog(true); } }
    }, 500);
  };
  const handleVariantSelectionChange = (reqIdx, selectedItemId) => {
    const req = variantRequirements.variant_requirements[reqIdx];
    if (selectedItemId === 'DECIDE_LATER') { setVariantSelections(p => ({ ...p, [reqIdx]: { variant_group_id: req.variant_group_id, variant_group_name: req.variant_group_name, bom_material_index: req.bom_material_index, selected_item_id: null, selected_item_name: null, selection_pending: true, quantity_per_unit: req.quantity_per_unit, unit: req.unit, price: 0 } })); return; }
    const si = req.available_items.find(i => i.item_id === selectedItemId);
    if (si) setVariantSelections(p => ({ ...p, [reqIdx]: { variant_group_id: req.variant_group_id, variant_group_name: req.variant_group_name, bom_material_index: req.bom_material_index, selected_item_id: si.item_id, selected_item_name: si.item_name, selection_pending: false, quantity_per_unit: req.quantity_per_unit, unit: req.unit, price: si.price } }));
  };
  const confirmVariantSelections = async () => {
    const reqCount = variantRequirements.variant_requirements.length;
    if (Object.keys(variantSelections).length < reqCount) { toast.error('Select all variants or "Decide Later"'); return; }
    const hasPending = Object.values(variantSelections).some(vs => vs.selection_pending);
    if (selectedLineItemForVariants !== null) {
      const upd = [...orderForm.line_items]; upd[selectedLineItemForVariants] = { ...upd[selectedLineItemForVariants], variant_selections: Object.values(variantSelections), needs_variant_selection: hasPending };
      if (editOrder) {
        try { await axios.patch(`${API_URL}/sales-orders/${editOrder.id}/update-variants`, [{ line_item_id: upd[selectedLineItemForVariants].id, variant_selections: Object.values(variantSelections) }], { headers: getAuthHeader() }); toast.success('Variants updated'); fetchOrders(); } catch (e) { toast.error('Failed'); return; }
      }
      setOrderForm({ ...orderForm, line_items: upd });
    } else {
      setOrderForm({ ...orderForm, line_items: [...orderForm.line_items, { ...pendingLineItem, variant_selections: Object.values(variantSelections), needs_variant_selection: hasPending }] });
      setCurrentItem({ item_id: '', quantity: '', unit_price: 0, sizes: '', specs: '', remarks: '', fulfillment_type: '' });
    }
    setOpenVariantDialog(false); setVariantRequirements(null); setVariantSelections({}); setPendingLineItem(null); setSelectedLineItemForVariants(null);
    if (!editOrder) toast.success(hasPending ? 'Added with pending variants' : 'Variants selected');
  };

  // =========== DISPATCH ===========
  const openDispatch = (order, li) => {
    const dispatched = li.dispatched_quantity || 0; const remaining = li.quantity - dispatched;
    setSelectedDispatchItem({ order_id: order.id, so_number: order.so_number, line_item_id: li.id, item_name: li.item_name, variant_code: li.variant_code, total_quantity: li.quantity, dispatched_quantity: dispatched, remaining_quantity: remaining });
    setDispatchForm({ quantity_dispatched: remaining, invoice_number: '', lr_number: '', dispatch_date: new Date().toISOString().split('T')[0], remarks: '' });
    setOpenDispatchDialog(true);
  };
  const handleDispatch = async (e) => {
    e.preventDefault(); if (!selectedDispatchItem) return;
    const qty = parseFloat(dispatchForm.quantity_dispatched);
    if (!dispatchForm.quantity_dispatched || isNaN(qty) || qty <= 0) { toast.error('Quantity is required'); return; }
    if (qty > selectedDispatchItem.remaining_quantity) { toast.error(`Max ${selectedDispatchItem.remaining_quantity}`); return; }
    try {
      await axios.post(`${API_URL}/sales-orders/${selectedDispatchItem.order_id}/line-items/${selectedDispatchItem.line_item_id}/dispatch`, { ...dispatchForm, quantity_dispatched: qty, dispatch_date: new Date(dispatchForm.dispatch_date).toISOString() }, { headers: getAuthHeader() });
      toast.success(`Dispatched ${qty} of ${selectedDispatchItem.item_name}`); setOpenDispatchDialog(false); setSelectedDispatchItem(null); fetchOrders();
    } catch (error) { const d = error.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed'); }
  };

  // =========== PAYMENT ===========
  const openPaymentDialog = (order) => { setPaymentOrder(order); setOpenPayment(true); };

  // =========== PRINT ===========
  const handlePrint = async (order) => {
    // Fetch items with images on-demand for printing (avoids loading 6MB on every page visit)
    try {
      const r = await axios.get(`${API_URL}/items?include_image=true`, { headers: getAuthHeader() });
      const itemsWithImages = r.data;
      // Enrich order with image data
      const enrichedOrder = enrichOrderWithMasterData(order);
      // Update line items with fresh images
      if (enrichedOrder.line_items) {
        enrichedOrder.line_items = enrichedOrder.line_items.map(li => {
          const freshItem = itemsWithImages.find(i => i.id === li.item_id);
          return { ...li, item_image: freshItem?.image_url || li.item_image };
        });
      }
      setPrintOrder(enrichedOrder);
      setOpenPrint(true);
    } catch (e) {
      // Fallback: print without images
      setPrintOrder(enrichOrderWithMasterData(order));
      setOpenPrint(true);
    }
  };
  const executePrint = () => {
    const el = printRef.current; if (!el) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>SO - ${printOrder?.so_number}</title><style>*{box-sizing:border-box}body{font-family:Arial;padding:15px;margin:0;font-size:13px;line-height:1.6}@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}.item-card{page-break-inside:avoid}}@page{size:A4;margin:15mm}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { w.print(); w.close(); }, 250);
  };

  // =========== DELETE ===========
  const handleDeleteOrder = async () => {
    if (!deleteConfirm.order) return;
    try { await axios.delete(`${API_URL}/sales-orders/${deleteConfirm.order.id}`, { headers: getAuthHeader() }); toast.success(`${deleteConfirm.order.so_number} deleted`); setDeleteConfirm({ open: false, type: null, order: null, lineItem: null }); navigateToRegister(); fetchOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteLineItem = async () => {
    if (!deleteConfirm.order || !deleteConfirm.lineItem) return;
    try { await axios.delete(`${API_URL}/sales-orders/${deleteConfirm.order.id}/line-items/${deleteConfirm.lineItem.id}`, { headers: getAuthHeader() }); toast.success('Line item deleted'); setDeleteConfirm({ open: false, type: null, order: null, lineItem: null }); fetchOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // =========== CONFIRM SO ===========
  const handleConfirmSO = async (order) => {
    try {
      const r = await axios.post(`${API_URL}/sales-orders/${order.id}/confirm`, {}, { headers: getAuthHeader() });
      const res = r.data;
      if (res.wos_created > 0) toast.success(`${res.wos_created} WO(s) created`);
      else if (res.skipped_items?.length > 0) toast.info('Confirmed. Skipped: ' + res.skipped_items.map(s => `${s.item_name} (${s.reason})`).join(', '));
      else toast.success('Confirmed');
      if (res.errors?.length > 0) toast.error('Errors: ' + res.errors.map(e => `${e.item_name}: ${e.error}`).join(', '));
      fetchOrders(); fetchWorkOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // =========== CHANGE ITEM ON LINE ITEM ===========
  const handleChangeItem = async () => {
    const { lineItem, newItemId, reason } = changeItemDialog;
    if (!newItemId || !reason.trim()) { toast.error('Select an item and provide a reason'); return; }
    setChangeItemDialog(d => ({ ...d, saving: true }));
    try {
      const r = await axios.patch(`${API_URL}/sales-orders/${activeSOId}/line-items/${lineItem.id}/change-item`, {
        new_item_id: newItemId, reason: reason.trim()
      }, { headers: getAuthHeader() });
      toast.success(r.data.message);
      if (r.data.wos_updated?.length > 0) toast.info(`WOs updated: ${r.data.wos_updated.join(', ')}`);
      setChangeItemDialog({ open: false, lineItem: null, newItemId: '', reason: '', saving: false });
      fetchOrders(); fetchWorkOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change item');
      setChangeItemDialog(d => ({ ...d, saving: false }));
    }
  };

  if (!user) return <DashboardLayout><div className="text-center py-12"><p className="text-muted-foreground">Please log in</p></div></DashboardLayout>;

  // =========== RENDER ===========
  const renderCurrentView = () => {
    // Level 3: Line Item Detail
    if (activeSOId && activeLineItemId && activeSO && activeLineItem) {
      const enriched = enrichOrderWithMasterData(activeSO);
      const enrichedLI = enriched.line_items?.find(li => li.id === activeLineItemId) || activeLineItem;
      return (
        <SOLineItemDetailView
          order={enriched} lineItem={enrichedLI} workOrders={workOrders}
          canSeePrices={canSeePrices} canEditSO={canEditSO}
          onBack={() => navigateToSO(activeSOId)}
          onDispatch={openDispatch}
          onEditVariants={() => handleEditSOForVariants(activeSO, activeLineItem, activeSO.line_items?.indexOf(activeLineItem))}
          onDeleteLineItem={() => setDeleteConfirm({ open: true, type: 'lineItem', order: activeSO, lineItem: activeLineItem })}
          hasItemBOM={hasItemBOM}
        />
      );
    }
    // Level 2: SO Detail
    if (activeSOId && activeSO) {
      const enriched = enrichOrderWithMasterData(activeSO);
      return (
        <SODetailView
          order={enriched} client={activeClient} workOrders={workOrders} woDeliveryStatus={woDeliveryStatus}
          canSeePrices={canSeePrices} canEditSO={canEditSO} canDeleteSO={canDeleteSO}
          onBack={navigateToRegister} onSelectLineItem={navigateToLineItem}
          onEdit={handleEdit} onPrint={handlePrint} onOpenPayment={openPaymentDialog} onConfirmSO={handleConfirmSO}
          onDeleteLineItem={(li) => setDeleteConfirm({ open: true, type: 'lineItem', order: activeSO, lineItem: li })}
          hasItemBOM={hasItemBOM}
          onChangeItem={(li) => setChangeItemDialog({ open: true, lineItem: li, newItemId: '', reason: '', saving: false })}
        />
      );
    }
    // Level 1: Register
    return (
      <SORegisterView
        orders={orders} clients={clients} workOrders={workOrders} woDeliveryStatus={woDeliveryStatus}
        canSeePrices={canSeePrices} canCreateSO={canCreateSO} canEditSO={canEditSO} canDeleteSO={canDeleteSO}
        isLoading={isLoading} searchTerm={searchTerm} statusFilter={statusFilter}
        currentPage={currentPage} totalPages={totalPages} totalOrders={totalOrders}
        onSelectSO={navigateToSO} onSearch={handleSearch} onStatusFilter={(v) => { setStatusFilter(v); setCurrentPage(1); fetchOrders(1, searchTerm, v); }}
        onPageChange={handlePageChange} onOpenCreate={() => { resetForm(); setOpenCreate(true); }} onOpenClient={() => setOpenClient(true)}
        onRefresh={() => { fetchOrders(); fetchWorkOrders(); toast.success('Refreshed'); }}
        onPrint={handlePrint} onOpenPayment={openPaymentDialog} onEdit={handleEdit}
        onDelete={async (order) => {
          // Fetch impact before showing dialog
          try {
            const impactRes = await axios.get(`${API_URL}/sales-orders/${order.id}/delete-impact`, { headers: getAuthHeader() });
            setDeleteConfirm({ open: true, type: 'order', order, lineItem: null, impact: impactRes.data, confirmText: '' });
          } catch (err) {
            // Fallback if impact API fails
            setDeleteConfirm({ open: true, type: 'order', order, lineItem: null, impact: null, confirmText: '' });
          }
        }}
        onFixVariantStatus={fixVariantStatus}
        calculateSOStatus={calculateSOStatus} enrichOrderWithMasterData={enrichOrderWithMasterData}
      />
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {renderCurrentView()}

        {/* =========== DIALOGS =========== */}

        {/* Create/Edit SO Dialog */}
        <Dialog open={openCreate} onOpenChange={(val) => { setOpenCreate(val); if (!val) resetForm(); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editOrder ? 'Edit' : 'Create'} Sales Order</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Client *</Label>
                    <SearchableSelect options={clients
                      .slice()
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(c => ({ value: c.id, label: c.name || 'Unknown' }))} value={orderForm.customer_id}
                      onValueChange={(v) => { const c = clients.find(c => c.id === v); setSelectedClient(c); setOrderForm({ ...orderForm, customer_id: v, delivery_address_id: '' }); }}
                      placeholder="Select client" searchPlaceholder="Search clients..." emptyText="No clients found" />
                  </div>
                  <div className="space-y-2"><Label>Delivery Address *</Label>
                    <select value={orderForm.delivery_address_id} onChange={(e) => setOrderForm({ ...orderForm, delivery_address_id: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" disabled={!selectedClient}>
                      <option value="">{selectedClient ? 'Select delivery address...' : 'Select client first'}</option>
                      {selectedClient?.delivery_addresses?.map((addr) => (<option key={addr.id || addr.city_code} value={addr.id || addr.city_code}>{addr.city_code || addr.city} - {addr.city}{addr.is_primary && ' (Primary)'}</option>))}
                      {selectedClient && (!selectedClient.delivery_addresses || selectedClient.delivery_addresses.length === 0) && (<option value="default">{selectedClient.city || selectedClient.address || 'Default'}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2"><Label>Delivery Date *</Label>
                    <Input type="date" value={orderForm.delivery_date} onChange={(e) => {
                      const sd = e.target.value; const today = new Date().toISOString().split('T')[0];
                      if (!editOrder && sd < today) { toast.error('Cannot be in the past'); return; }
                      if (editOrder && sd < today) { toast.error('Cannot be in the past'); return; }
                      const tpd = new Date(sd); tpd.setDate(tpd.getDate() - 5);
                      setOrderForm({ ...orderForm, delivery_date: sd, target_production_date: tpd.toISOString().split('T')[0] });
                    }} min={editOrder ? undefined : new Date().toISOString().split('T')[0]} required />
                  </div>
                  <div className="space-y-2"><Label>Target Production Date *</Label>
                    <Input type="date" value={orderForm.target_production_date} onChange={(e) => {
                      if (orderForm.delivery_date && e.target.value > orderForm.delivery_date) { toast.error('Cannot be after delivery date'); return; }
                      setOrderForm({ ...orderForm, target_production_date: e.target.value });
                    }} max={orderForm.delivery_date || undefined} required className={orderForm.target_production_date && orderForm.target_production_date < new Date().toISOString().split('T')[0] ? 'border-orange-400 bg-orange-50' : ''} />
                    {orderForm.target_production_date && orderForm.target_production_date < new Date().toISOString().split('T')[0] && (<p className="text-xs text-orange-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Target date in past</p>)}
                  </div>
                </div>

                {/* Client + address info */}
                {selectedClient && orderForm.delivery_address_id && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg grid grid-cols-2 gap-4 text-sm">
                    <div><p className="text-xs text-muted-foreground">Client</p><p className="font-medium">{selectedClient.name}</p>{selectedClient.gst_number && <p className="text-xs text-muted-foreground">GST: {selectedClient.gst_number}</p>}</div>
                    <div><p className="text-xs text-muted-foreground">Delivery</p>{(() => {
                      const addr = selectedClient.delivery_addresses?.find(a => (a.id || a.city_code) === orderForm.delivery_address_id);
                      if (!addr && orderForm.delivery_address_id === 'default') return <p className="font-medium">{selectedClient.address || 'Default'}</p>;
                      if (addr) return <><p className="font-medium">{addr.city_code || addr.city}</p><p className="text-xs">{addr.address}</p></>;
                      return <p className="text-muted-foreground">Not found</p>;
                    })()}</div>
                  </div>
                )}

                {/* Add line item */}
                <div className="border rounded-lg p-4 space-y-3">
                  <Label className="font-semibold">Add Line Item</Label>
                  {/* Column headers */}
                  <div className={`grid ${canSeePrices ? 'grid-cols-[1fr_100px_100px_80px]' : 'grid-cols-[1fr_100px_80px]'} gap-3 text-xs text-muted-foreground font-medium`}>
                    <span>Item</span>
                    <span>Qty</span>
                    {canSeePrices && <span>Price</span>}
                    <span></span>
                  </div>
                  <div className={`grid ${canSeePrices ? 'grid-cols-[1fr_100px_100px_80px]' : 'grid-cols-[1fr_100px_80px]'} gap-3`}>
                    <SearchableSelect options={finishedGoods.map(item => ({ 
                      value: item.id, 
                      label: `${item.name} ${hasItemBOM(item.id) ? '✓ BOM' : ''}`,
                      code: item.item_code || '',
                      category: item.category || ''
                    }))} value={currentItem.item_id}
                      onValueChange={(v) => { const it = finishedGoods.find(i => i.id === v); setCurrentItem({ ...currentItem, item_id: v, unit_price: it?.price || 0, specs: it?.specs ? formatSpecs(it.specs) : '' }); }}
                      placeholder="Select item" searchPlaceholder="Search items..." emptyText="No items" />
                    <Input type="number" placeholder="Qty" value={currentItem.quantity} onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })} className="w-full" />
                    {canSeePrices && <Input type="number" placeholder="Price" value={currentItem.unit_price} onChange={(e) => setCurrentItem({ ...currentItem, unit_price: parseFloat(e.target.value) || 0 })} className="w-full" />}
                    <Button type="button" onClick={addLineItem} disabled={!currentItem.item_id} className="w-full">Add</Button>
                  </div>
                  {currentItem.item_id && (
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Specs</Label><Textarea placeholder="Specifications..." value={currentItem.specs} onChange={(e) => setCurrentItem({ ...currentItem, specs: e.target.value })} rows={3} className="text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Remarks</Label><Textarea placeholder="Production notes..." value={currentItem.remarks} onChange={(e) => setCurrentItem({ ...currentItem, remarks: e.target.value })} rows={3} className="text-sm" /></div>
                    </div>
                  )}
                </div>

                {/* Line items table in form */}
                {orderForm.line_items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-center w-20">Qty</TableHead><TableHead className="w-24">Fulfillment</TableHead><TableHead className="w-56">Specs</TableHead><TableHead className="w-56">Remarks</TableHead>{canSeePrices && <TableHead className="text-right w-24">Price</TableHead>}{canSeePrices && <TableHead className="text-right w-24">Amount</TableHead>}<TableHead className="w-10"></TableHead></TableRow></TableHeader>
                      <TableBody>
                        {orderForm.line_items.map((item, idx) => {
                          const ft = item.fulfillment_type || (item.item_source_type === 'IP' ? 'Make' : 'Buy');
                          const bomStatus = getItemBOMStatus(item.item_id, ft, item.variant_selections);
                          return (
                            <React.Fragment key={idx}>
                              <TableRow className={bomStatus.status !== 'ok' ? 'bg-orange-50/50' : ''}>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex items-center flex-wrap gap-1">{item.item_name}
                                      {item.variant_selections?.length > 0 && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700"><Layers className="w-3 h-3 mr-1" />{item.variant_selections.length}</Badge>}
                                      {item.is_fulfillment_override && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />Override</Badge>}
                                    </div>
                                    {bomStatus.status === 'no_bom' && <Badge className="text-xs bg-orange-100 text-orange-800"><AlertCircle className="w-3 h-3 mr-1" />No BOM</Badge>}
                                    {bomStatus.status === 'needs_variants' && <div className="flex items-center gap-1"><Badge className="text-xs bg-amber-100 text-amber-800"><Layers className="w-3 h-3 mr-1" />Needs Variants</Badge><Button type="button" variant="link" size="sm" className="text-xs h-auto p-0 text-blue-600" onClick={() => handleSelectVariantsForLineItem(idx)}>Select →</Button></div>}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center"><Input type="number" className="w-16 h-8 text-center" value={item.quantity} onChange={(e) => { const q = parseInt(e.target.value) || ''; const upd = [...orderForm.line_items]; upd[idx] = { ...upd[idx], quantity: q, amount: (parseInt(q) || 0) * upd[idx].unit_price }; setOrderForm({ ...orderForm, line_items: upd }); }} min={1} /></TableCell>
                                <TableCell>
                                  <Select value={ft} onValueChange={(v) => handleFulfillmentTypeChange(idx, v)}>
                                    <SelectTrigger className={`h-8 text-xs ${item.is_fulfillment_override ? 'border-amber-400 bg-amber-50' : ''}`}><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="Make"><div className="flex items-center gap-1"><Factory className="w-3 h-3 text-orange-600" />Make</div></SelectItem><SelectItem value="Buy"><div className="flex items-center gap-1"><ShoppingCart className="w-3 h-3 text-purple-600" />Buy</div></SelectItem></SelectContent>
                                  </Select>
                                  <p className="text-[10px] text-muted-foreground mt-1">Default: {item.item_source_type === 'IP' ? 'Make' : 'Buy'}</p>
                                </TableCell>
                                <TableCell><Textarea className="min-h-[70px] text-xs resize-y w-full" placeholder="Specs..." value={typeof item.specs === 'object' ? Object.entries(item.specs || {}).map(([k, v]) => `${k}: ${v}`).join('\n') : (item.specs || '')} onChange={(e) => { const upd = [...orderForm.line_items]; upd[idx] = { ...upd[idx], specs: e.target.value }; setOrderForm({ ...orderForm, line_items: upd }); }} rows={3} /></TableCell>
                                <TableCell><Textarea className="min-h-[70px] text-xs resize-y w-full" placeholder="Remarks..." value={typeof item.remarks === 'object' ? '' : (item.remarks || '')} onChange={(e) => { const upd = [...orderForm.line_items]; upd[idx] = { ...upd[idx], remarks: e.target.value }; setOrderForm({ ...orderForm, line_items: upd }); }} rows={3} /></TableCell>
                                {canSeePrices && <TableCell className="text-right"><Input type="number" className="w-20 h-8 text-right" value={item.unit_price} onChange={(e) => { const p = parseFloat(e.target.value) || 0; const upd = [...orderForm.line_items]; upd[idx] = { ...upd[idx], unit_price: p, amount: upd[idx].quantity * p }; setOrderForm({ ...orderForm, line_items: upd }); }} min={0} step="0.01" /></TableCell>}
                                {canSeePrices && <TableCell className="text-right font-medium text-sm">{formatCurrency(item.amount)}</TableCell>}
                                <TableCell><Button variant="ghost" size="sm" onClick={() => removeLineItem(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button></TableCell>
                              </TableRow>
                              {item.variant_selections?.map((vs, vi) => (
                                <TableRow key={`${idx}-vs-${vi}`} className="bg-purple-50/50 text-xs">
                                  <TableCell colSpan={5} className="pl-6"><span className="text-purple-600">↳</span><span className="ml-2 font-medium">{vs.variant_group_name}:</span><span className="ml-1">{vs.selected_item_name}</span><span className="ml-2 text-muted-foreground">({vs.quantity_per_unit} {vs.unit}/unit)</span></TableCell>
                                  {canSeePrices && <TableCell className="text-right text-muted-foreground">{formatRate(vs.price)}</TableCell>}{canSeePrices && <TableCell></TableCell>}<TableCell></TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        <TableRow className="bg-muted/50"><TableCell colSpan={canSeePrices ? 6 : 5} className="font-semibold text-right">Total:</TableCell>{canSeePrices && <TableCell className="font-semibold text-right">{formatCurrency(orderForm.line_items.reduce((s, i) => s + (i.amount || 0), 0))}</TableCell>}<TableCell></TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <DialogFooter><Button type="submit" disabled={!orderForm.customer_id || orderForm.line_items.length === 0}>{editOrder ? 'Update' : 'Create'} Order</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Variant Dialog */}
        <Dialog open={openVariantDialog} onOpenChange={(v) => { setOpenVariantDialog(v); if (!v) { setVariantRequirements(null); setVariantSelections({}); setPendingLineItem(null); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-purple-600" />Select Variant Materials</DialogTitle><DialogDescription>Select items for each variant, or "Decide Later".</DialogDescription></DialogHeader>
            {variantRequirements && (
              <div className="space-y-4 py-2">
                <div className="p-3 bg-muted rounded-lg"><p className="font-medium text-sm">{pendingLineItem?.item_name}</p><p className="text-xs text-muted-foreground">Qty: {pendingLineItem?.quantity}{canSeePrices && ` × ₹${pendingLineItem?.unit_price}`}</p></div>
                {variantRequirements.variant_requirements.map((req, idx) => (
                  <div key={idx} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2"><Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">{req.variant_group_name}</Badge><span className="text-xs text-muted-foreground">{req.quantity_per_unit} {req.unit}/unit</span></div>
                    <select className="w-full h-9 px-3 py-1 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                      value={variantSelections[idx]?.selection_pending ? 'DECIDE_LATER' : (variantSelections[idx]?.selected_item_id || '')}
                      onChange={(e) => e.target.value && handleVariantSelectionChange(idx, e.target.value)}>
                      <option value="">Select {req.variant_group_name}</option>
                      {req.available_items.map((item) => (<option key={item.item_id} value={item.item_id}>{item.item_name} - {formatCurrency(item.price)}/{item.unit}</option>))}
                      <option value="DECIDE_LATER">Decide Later</option>
                    </select>
                  </div>
                ))}
                {Object.keys(variantSelections).length > 0 && (
                  <div className="p-2 bg-purple-50 rounded-lg text-xs"><p className="font-medium text-purple-700 mb-1">Selected:</p>
                    {Object.entries(variantSelections).map(([idx, vs]) => (<p key={idx} className={vs.selection_pending ? 'text-amber-600' : 'text-purple-600'}>{vs.variant_group_name}: {vs.selection_pending ? 'Decide Later' : vs.selected_item_name}</p>))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="gap-2 pt-2"><Button variant="outline" size="sm" onClick={() => setOpenVariantDialog(false)}>Cancel</Button><Button size="sm" onClick={confirmVariantSelections} disabled={Object.keys(variantSelections).length < (variantRequirements?.variant_requirements?.length || 0)}>Confirm</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dispatch Dialog */}
        <Dialog open={openDispatchDialog} onOpenChange={setOpenDispatchDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-teal-600" />Record Dispatch</DialogTitle>
              <DialogDescription>{selectedDispatchItem && (<><strong>{selectedDispatchItem.item_name}</strong> from SO #{selectedDispatchItem.so_number}
                {selectedDispatchItem.variant_code && selectedDispatchItem.variant_code !== selectedDispatchItem.item_name?.toUpperCase() && (<div className="mt-1 font-mono text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded inline-block">{selectedDispatchItem.variant_code}</div>)}
                <div className="mt-2 text-sm">Total: {selectedDispatchItem.total_quantity} | Dispatched: {selectedDispatchItem.dispatched_quantity} | <span className="text-teal-600 font-medium">Remaining: {selectedDispatchItem.remaining_quantity}</span></div></>)}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDispatch}>
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Quantity *</Label><Input type="number" value={dispatchForm.quantity_dispatched} onChange={(e) => setDispatchForm({ ...dispatchForm, quantity_dispatched: e.target.value })} max={selectedDispatchItem?.remaining_quantity} min={1} required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Invoice No.</Label><Input value={dispatchForm.invoice_number} onChange={(e) => setDispatchForm({ ...dispatchForm, invoice_number: e.target.value.toUpperCase() })} placeholder="INV-001" style={{ textTransform: 'uppercase' }} /></div>
                  <div className="space-y-2"><Label>LR No.</Label><Input value={dispatchForm.lr_number} onChange={(e) => setDispatchForm({ ...dispatchForm, lr_number: e.target.value.toUpperCase() })} placeholder="LR-001" style={{ textTransform: 'uppercase' }} /></div>
                </div>
                <div className="space-y-2"><Label>Dispatch Date *</Label><Input type="date" value={dispatchForm.dispatch_date} onChange={(e) => setDispatchForm({ ...dispatchForm, dispatch_date: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Remarks</Label><Input value={dispatchForm.remarks} onChange={(e) => setDispatchForm({ ...dispatchForm, remarks: e.target.value.toUpperCase() })} style={{ textTransform: 'uppercase' }} /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenDispatchDialog(false)}>Cancel</Button><Button type="submit" className="bg-teal-600 hover:bg-teal-700"><Truck className="w-4 h-4 mr-2" />Dispatch</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={openPayment} onOpenChange={setOpenPayment}>
          <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" />Payment - {paymentOrder?.so_number}</DialogTitle></DialogHeader>
            {paymentOrder && (
              <div className="flex-1 overflow-y-auto space-y-4 py-2">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center"><div><p className="text-sm text-muted-foreground">Amount</p><p className="text-2xl font-bold text-blue-700">{formatCurrency(paymentOrder.total_amount || 0)}</p></div><div className="text-right"><p className="text-sm text-muted-foreground">Balance</p><p className={`text-xl font-bold ${(paymentOrder.balance_due || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>{formatCurrency(paymentOrder.balance_due || (paymentOrder.total_amount - paymentOrder.total_advance) || 0)}</p></div></div>
                </div>
                <div className="border rounded-lg"><div className="p-3 bg-muted/50 border-b"><p className="text-sm font-semibold">Payments</p></div>
                  {paymentOrder.advances?.length > 0 ? (
                    <Table><TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Date</TableHead><TableHead>Receipt</TableHead><TableHead>Mode</TableHead><TableHead>Ref</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                      <TableBody>{paymentOrder.advances.map((adv, idx) => (<TableRow key={adv.id || idx}><TableCell className="text-muted-foreground">{idx + 1}</TableCell><TableCell>{adv.payment_date || '-'}</TableCell><TableCell>{adv.remarks?.includes('Receipt') ? <span className="text-blue-600 font-medium">{adv.remarks.replace('From Receipt ', '')}</span> : <span className="text-xs text-muted-foreground">Legacy</span>}</TableCell><TableCell className="capitalize">{adv.payment_mode?.replace('_', ' ') || '-'}</TableCell><TableCell className="text-xs">{adv.reference_number || '-'}</TableCell><TableCell className="text-right font-medium text-green-600">{formatCurrency(adv.amount || 0)}</TableCell></TableRow>))}
                        <TableRow className="bg-green-50 font-semibold"><TableCell colSpan={5} className="text-right">Total:</TableCell><TableCell className="text-right text-green-700">{formatCurrency(paymentOrder.total_advance || 0)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  ) : (<div className="p-6 text-center text-muted-foreground"><DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No payments yet</p><p className="text-xs mt-1">Record via Bank Transactions → Sales Receipts</p></div>)}
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"><p className="font-medium">Payment Recording</p><p className="text-xs mt-1">Go to <strong>Accounts → Bank Transactions</strong> → create receipt → allocate to this SO via <strong>Sales Receipts</strong>.</p></div>
              </div>
            )}
            <DialogFooter className="border-t pt-4 mt-2"><Button variant="outline" onClick={() => setOpenPayment(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Print Dialog */}
        <Dialog open={openPrint} onOpenChange={setOpenPrint}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" />Print SO</DialogTitle></DialogHeader>
            <SOPrint ref={printRef} order={printOrder} clients={clients} items={items} />
            <DialogFooter><Button variant="outline" onClick={() => setOpenPrint(false)}>Close</Button><Button onClick={executePrint}><Printer className="w-4 h-4 mr-2" />Print</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fulfillment Override Warning */}
        <AlertDialog open={showFulfillmentWarning} onOpenChange={setShowFulfillmentWarning}>
          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" />Fulfillment Override</AlertDialogTitle>
            <AlertDialogDescription asChild><div className="space-y-3"><p>Changing fulfillment type contradicts default source.</p>
              {pendingFulfillmentChange && (<div className="bg-muted p-3 rounded-lg space-y-2"><p><strong>Item:</strong> {pendingFulfillmentChange.lineItem.item_name}</p><div className="flex items-center gap-2"><Badge variant="outline">Default: {pendingFulfillmentChange.item?.source_type === 'IP' ? 'Make' : 'Buy'}</Badge><span>→</span><Badge className={pendingFulfillmentChange.newFulfillmentType === 'Make' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}>Override: {pendingFulfillmentChange.newFulfillmentType}</Badge></div></div>)}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"><strong>Result:</strong> Fulfillment task created for {pendingFulfillmentChange?.newFulfillmentType === 'Make' ? 'Production' : 'Purchase'}.</div>
            </div></AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel onClick={() => { setShowFulfillmentWarning(false); setPendingFulfillmentChange(null); }}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { if (pendingFulfillmentChange) { updateLineItemFulfillment(pendingFulfillmentChange.idx, pendingFulfillmentChange.newFulfillmentType, true); toast.info('Fulfillment task created on save'); } setShowFulfillmentWarning(false); setPendingFulfillmentChange(null); }} className="bg-amber-600 hover:bg-amber-700"><AlertTriangle className="w-4 h-4 mr-2" />Confirm</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Stock Available Dialog */}
        <AlertDialog open={showStockAvailableDialog} onOpenChange={setShowStockAvailableDialog}>
          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="flex items-center gap-2 text-blue-600"><Info className="w-5 h-5" />Stock Available</AlertDialogTitle>
            <AlertDialogDescription asChild><div className="space-y-3"><p><strong>&quot;{stockAvailableData?.lineItem?.item_name}&quot;</strong> has sufficient stock.</p>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 grid grid-cols-2 gap-2 text-sm"><div><span className="text-muted-foreground">Free Stock:</span><span className="ml-2 font-semibold text-blue-700">{stockAvailableData?.freeStock}</span></div><div><span className="text-muted-foreground">Order:</span><span className="ml-2 font-semibold">{stockAvailableData?.orderQty}</span></div></div>
              <p className="text-sm text-muted-foreground">Still proceed with &quot;Buy&quot;?</p>
            </div></AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel onClick={() => { setShowStockAvailableDialog(false); setStockAvailableData(null); }}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { if (stockAvailableData) { updateLineItemFulfillment(stockAvailableData.idx, stockAvailableData.newFulfillmentType, true); toast.info('Fulfillment task on save'); } setShowStockAvailableDialog(false); setStockAvailableData(null); }} className="bg-blue-600 hover:bg-blue-700">Proceed</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation - Enhanced with Impact Summary */}
        <AlertDialog open={deleteConfirm.open} onOpenChange={(o) => !o && setDeleteConfirm({ open: false, type: null, order: null, lineItem: null, impact: null, confirmText: '' })}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                {deleteConfirm.type === 'order' ? 'Delete Sales Order' : 'Delete Line Item'}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  {deleteConfirm.type === 'order' ? (
                    <>
                      <p>Delete <strong>{deleteConfirm.order?.so_number}</strong>?</p>
                      
                      {/* Impact Summary */}
                      {deleteConfirm.impact && (
                        <div className="bg-slate-50 p-3 rounded-lg border space-y-2 text-sm">
                          <p className="font-medium text-slate-700">This will permanently delete:</p>
                          <ul className="list-disc ml-5 space-y-1 text-slate-600">
                            <li><strong>{deleteConfirm.impact.line_items_count}</strong> line item(s)</li>
                            <li><strong>{deleteConfirm.impact.work_orders_count}</strong> work order(s)
                              {deleteConfirm.impact.work_orders?.length > 0 && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({deleteConfirm.impact.work_orders.map(w => w.work_order_number).join(', ')})
                                </span>
                              )}
                            </li>
                            {deleteConfirm.impact.materials_to_unblock?.length > 0 && (
                              <li><strong>{deleteConfirm.impact.materials_to_unblock.length}</strong> material(s) will be unblocked</li>
                            )}
                            {deleteConfirm.impact.indents_affected > 0 && (
                              <li><strong>{deleteConfirm.impact.indents_affected}</strong> indent(s) will be cancelled</li>
                            )}
                            {deleteConfirm.impact.pos_affected > 0 && (
                              <li><strong>{deleteConfirm.impact.pos_affected}</strong> PO(s) will be affected</li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      {/* Block reason */}
                      {deleteConfirm.impact?.block_reason && (
                        <div className="bg-red-50 p-3 rounded-lg border border-red-200 text-sm text-red-700">
                          <strong>Cannot delete:</strong> {deleteConfirm.impact.block_reason}
                        </div>
                      )}
                      
                      {/* Type DELETE to confirm */}
                      {deleteConfirm.impact?.can_delete !== false && (
                        <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                          <p className="text-sm text-red-700 font-medium mb-2">Type "DELETE" to confirm:</p>
                          <input 
                            type="text"
                            placeholder="Type DELETE"
                            value={deleteConfirm.confirmText || ''}
                            onChange={(e) => setDeleteConfirm(prev => ({ ...prev, confirmText: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p>Delete <strong>{deleteConfirm.lineItem?.item_name}</strong> ({deleteConfirm.lineItem?.quantity} pcs)?</p>
                      <p className="text-red-600">Also deletes associated WO.</p>
                    </>
                  )}
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                className="bg-red-600 hover:bg-red-700" 
                onClick={deleteConfirm.type === 'order' ? handleDeleteOrder : handleDeleteLineItem}
                disabled={
                  deleteConfirm.impact?.can_delete === false || 
                  (deleteConfirm.type === 'order' && deleteConfirm.confirmText !== 'DELETE')
                }
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Client Form */}
        <ClientForm open={openClient} onOpenChange={setOpenClient} editClient={null} onSuccess={fetchClients} getAuthHeader={getAuthHeader} />

        {/* Change Item Dialog */}
        <Dialog open={changeItemDialog.open} onOpenChange={(o) => !o && setChangeItemDialog({ open: false, lineItem: null, newItemId: '', reason: '', saving: false })}>
          <DialogContent className="max-w-lg" data-testid="change-item-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                Change Item
              </DialogTitle>
              <DialogDescription>
                Replace the item on this SO line item. Linked WOs will be updated automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">Current: {changeItemDialog.lineItem?.item_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Line: {changeItemDialog.lineItem?.item_code} | Qty: {changeItemDialog.lineItem?.quantity}
                </p>
              </div>
              <div className="space-y-2">
                <Label>New Item *</Label>
                <SearchableSelect
                  options={items
                    .filter(i => {
                      if (!changeItemDialog.lineItem) return false;
                      if (i.id === changeItemDialog.lineItem.item_id) return false;
                      // Filter: same bom_id, or both FNG/SFG if no BOM
                      const currentItem = items.find(it => it.id === changeItemDialog.lineItem.item_id);
                      const currentBomId = currentItem?.bom_id;
                      if (currentBomId) return i.bom_id === currentBomId;
                      // No BOM: show all FNG/SFG items
                      return ['FNG', 'SFG'].includes(i.category);
                    })
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(i => ({
                      value: i.id,
                      label: i.name,
                      code: i.item_code || '',
                      category: i.category || '',
                      searchTerms: [i.name || '', i.item_code || '']
                    }))}
                  value={changeItemDialog.newItemId}
                  onValueChange={(v) => setChangeItemDialog(d => ({ ...d, newItemId: v }))}
                  placeholder="Search item by name or code..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-red-600 font-semibold">Reason *</Label>
                <Textarea
                  data-testid="change-item-reason"
                  value={changeItemDialog.reason}
                  onChange={(e) => setChangeItemDialog(d => ({ ...d, reason: e.target.value }))}
                  rows={2}
                  placeholder="Why is this item being changed?"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setChangeItemDialog({ open: false, lineItem: null, newItemId: '', reason: '', saving: false })}>Cancel</Button>
              <Button
                data-testid="change-item-submit"
                onClick={handleChangeItem}
                disabled={changeItemDialog.saving || !changeItemDialog.newItemId || !changeItemDialog.reason.trim()}
              >
                {changeItemDialog.saving ? 'Saving...' : 'Change Item'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
